import { Dispatch } from 'react-redux';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import RoutesUtils from '../../utils/routes';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import { PublicationSource, PublicationCreationProgress, PublicationCreationStatus, SelectedTemplate } from './reducer';
import { createNotification } from '../NotificationBar/actions';
import { getOrFetchDriveItemMeta, receiveTemplateItems } from '../DriveItemList/actions';
import { FileError } from '../ImportManager/actions';
import { openNoSaveAsPopup } from '../NoSaveAsPopup/actions';
import { addCustomTemplate, removeCustomTemplate } from '../DriveItemList/actions';
import { setParentFolderId } from '../Editor/actions';
import DriveItemUtils from '../../utils/driveItem';

export enum PublicationCreatorActionKeys {
	GET_PUBLICATION_TEMPLATES = '@@publicationCreator/GET_PUBLICATION_TEMPLATES',
	SET_PUBLICATION_TEMPLATES = '@@publicationCreator/SET_PUBLICATION_TEMPLATES',
	SHOW = '@@publicationCreator/SHOW',
	HIDE = '@@publicationCreator/HIDE',
	SELECT_TEMPLATE = '@@publicationCreator/SELECT_TEMPLATE',
	SET_NAME = '@@publicationCreator/SET_NAME',
	SET_ERROR = '@@publicationCreator/SET_ERROR',
	SET_REQUEST_ID = '@@publicationCreator/SET_REQUEST_ID',
	SET_CREATION_STATUS = '@@publicationCreator/SET_CREATION_STATUS',
	UPDATE_CREATION_PROGRESS = '@@publicationCreator/UPDATE_CREATION_PROGRESS',
	REMOVE_FROM_CREATION_PROGRESS = '@@publicationCreator/REMOVE_FROM_CREATION_PROGRESS',
	SET_AS_TEMPLATE = '@@publicationCreator/SET_AS_TEMPLATE',
	REMOVE_TEMPLATE_FROM_STORE = '@@publicationCreator/REMOVE_TEMPLATE_FROM_STORE',
}

interface GetPublicationTemplatesAction extends Action {
	type: PublicationCreatorActionKeys.GET_PUBLICATION_TEMPLATES;
}

interface SetPublicationTemplatesAction extends Action {
	type: PublicationCreatorActionKeys.SET_PUBLICATION_TEMPLATES;
	masterTemplates: Api.DriveItem[];
	customTemplates: Api.DriveItem[];
}

interface ShowPublicationCreatorAction extends Action {
	type: PublicationCreatorActionKeys.SHOW;
	source: PublicationSource;
}

interface HidePublicationCreatorAction extends Action {
	type: PublicationCreatorActionKeys.HIDE;
}

interface SelectTemplateAction extends Action {
	type: PublicationCreatorActionKeys.SELECT_TEMPLATE;
	data: SelectedTemplate;
}

interface SetNameAction extends Action {
	type: PublicationCreatorActionKeys.SET_NAME;
	name: string;
}

interface SetErrorAction extends Action {
	type: PublicationCreatorActionKeys.SET_ERROR;
	error: string | null;
}

interface SetRequestIdAction extends Action {
	type: PublicationCreatorActionKeys.SET_REQUEST_ID;
	requestId: string;
}

interface SetCreationStatusAction extends Action {
	type: PublicationCreatorActionKeys.SET_CREATION_STATUS;
	status: PublicationCreationStatus;
}

interface UpdateCreationProgressAction extends Action {
	type: PublicationCreatorActionKeys.UPDATE_CREATION_PROGRESS;
	data: PublicationCreationProgress;
}

interface RemoveFromCreationProgressAction extends Action {
	type: PublicationCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS;
}

interface RemoveTemplateFromStoreAction extends Action {
	type: PublicationCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE;
	id: number;
}

export type PublicationCreatorAction =
	GetPublicationTemplatesAction |
	SetPublicationTemplatesAction |
	ShowPublicationCreatorAction |
	HidePublicationCreatorAction |
	SelectTemplateAction |
	SetNameAction |
	SetErrorAction |
	SetRequestIdAction |
	SetCreationStatusAction |
	UpdateCreationProgressAction |
	RemoveFromCreationProgressAction |
	RemoveTemplateFromStoreAction;

export const setPublicationTemplates = (masterTemplates: Api.DriveItem[], customTemplates: Api.DriveItem[]): SetPublicationTemplatesAction => ({
	type: PublicationCreatorActionKeys.SET_PUBLICATION_TEMPLATES,
	masterTemplates,
	customTemplates,
});

export const showPublicationCreator = (source: PublicationSource): ShowPublicationCreatorAction => ({
	type: PublicationCreatorActionKeys.SHOW,
	source,
});

export const hidePublicationCreator = () => (dispatch: Dispatch<StoreState>) => {
	dispatch(removeFromCreationProgress());
	dispatch({
		type: PublicationCreatorActionKeys.HIDE,
	});
};

export const selectTemplate = (data: SelectedTemplate): SelectTemplateAction => ({
	type: PublicationCreatorActionKeys.SELECT_TEMPLATE,
	data,
});

export const setName = (name: string): SetNameAction => ({
	type: PublicationCreatorActionKeys.SET_NAME,
	name,
});

export const setError = (error: string | null): SetErrorAction => ({
	type: PublicationCreatorActionKeys.SET_ERROR,
	error,
});

export const setCreationStatus = (status: PublicationCreationStatus): SetCreationStatusAction => ({
	type: PublicationCreatorActionKeys.SET_CREATION_STATUS,
	status,
});

export const openPublicationCreatorForSaveAs = ({ id, label, format }: Api.DriveItem) => (dispatch: Dispatch<StoreState>) => {
	dispatch(showPublicationCreator(PublicationSource.PUBLICATION));
	dispatch(selectTemplate({
		id,
		label,
		format,
	}));
};

export const updateCreationProgress = (data: PublicationCreationProgress): UpdateCreationProgressAction => ({
	type: PublicationCreatorActionKeys.UPDATE_CREATION_PROGRESS,
	data,
});

export const removeFromCreationProgress = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		publicationCreator: { requestId },
	} = getState();

	dispatch({
		type: PublicationCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS,
	});

	if (requestId) {
		Utils.cancelRequest(requestId);
	}
};

export const getPublicationTemplates = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		dispatch({
			type: PublicationCreatorActionKeys.GET_PUBLICATION_TEMPLATES,
		});

		const templatesData = await ApiUtils.getPublicationTemplates();
		await dispatch(receiveTemplateItems(templatesData));

		const {
			masterProposals: masterTemplates,
			customTemplates,
		} = templatesData;
		dispatch(setPublicationTemplates(masterTemplates, customTemplates));
	} catch (error) {
		dispatch(hidePublicationCreator());
		dispatch(createNotification('error.fetch_publication_templates_error'));
	}
};

export const createPublicationFromMasterTemplate = (
	publicationGId: number,
	label: string,
	format?: Api.PublicationFormat,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const errorMessage = DriveItemUtils.isProposal(format)
		? 'error.create_proposal_error'
		: 'error.create_publication_error';

	try {
		const {
			galleryInfos: { config: { clientId, applicationGallery } },
		} = getState();

		const folderId = Utils.getFolderIdForNewResource();

		if (!folderId) {
			return dispatch(createNotification(errorMessage));
		}

		const { resource } = await ApiUtils.createPublicationFromMasterTemplate(clientId, applicationGallery, folderId, publicationGId, label);
		const { globalId } = resource.referent || resource;
		dispatch(hidePublicationCreator());
		dispatch(setParentFolderId(folderId));
		RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));
		await dispatch(getOrFetchDriveItemMeta(folderId, true, true, true));
	} catch (error) {
		if (ApiUtils.isDuplicateFileNameError(error.responseText)) {
			dispatch(setError('error.duplicate_file_name'));
		} else if (error.responseText.includes('PROP_SAVE_FORBIDDEN')) {
			dispatch(hidePublicationCreator());
			dispatch(openNoSaveAsPopup());
		} else {
			dispatch(hidePublicationCreator());
			return dispatch(createNotification(errorMessage));
		}
	}
};

const getPublicationCreationProgress = (
	folderId: number,
	clientId: number,
	globalId: number,
	status: Api.AssetItemStatus,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const onProgressUpdate = (percentage: number) => {
			dispatch(updateCreationProgress({
				percentage,
			}));
		};

		const onProgressError = () => {
			dispatch(updateCreationProgress({
				error: FileError.PROCESSING,
				percentage: 100,
			}));
		};

		const onProgressFinish = async () => {
			dispatch(updateCreationProgress({
				percentage: 100,
			}));
			await dispatch(getOrFetchDriveItemMeta(folderId, true, true, true));
			dispatch(hidePublicationCreator());
			RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));
		};

		const shouldCancelProgress = (state: StoreState) => {
			return !state.publicationCreator.requestId;
		};

		const onProgressCancel = async () => {
			await ApiUtils.trashResources(clientId, [globalId]);
		};

		if (![
			Api.AssetItemStatus.LIVE,
			Api.AssetItemStatus.ERROR,
		].includes(status)) {
			await ApiUtils.getPublicationProgress(clientId, globalId, onProgressUpdate, onProgressError, onProgressFinish, shouldCancelProgress, onProgressCancel);
		} else {
			if (status === Api.AssetItemStatus.LIVE) {
				onProgressFinish();
			}

			if (status === Api.AssetItemStatus.ERROR) {
				onProgressError();
			}
		}
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const createProposalFromDesktopFile = (
	file: File,
	isCorrectFileType: boolean,
	requestId: string,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { publicationCreator: { fromFile } } = getState();
		// to remove previously selected file (e.g., with wrong type)
		if (fromFile.status) {
			dispatch(removeFromCreationProgress());
		}

		dispatch({
			type: PublicationCreatorActionKeys.SET_REQUEST_ID,
			requestId,
		});
		dispatch(setCreationStatus(PublicationCreationStatus.IMPORTING));

		if (!isCorrectFileType) {
			dispatch(updateCreationProgress({
				error: FileError.TYPE,
				percentage: 100,
			}));
			return;
		}

		const {
			galleryInfos: { config: { clientId } },
		} = getState();

		const folderId = Utils.getFolderIdForNewResource();

		if (!folderId) {
			dispatch(hidePublicationCreator());
			return dispatch(createNotification('error.create_proposal_error'));
		}

		const onImportProgress = (e: ProgressEvent) => {
			const percentage = Math.floor((e.loaded / e.total) * 100);
			dispatch(updateCreationProgress({
				percentage,
			}));
		};

		const { globalId, status } = await ApiUtils.createProposalFromDesktopFile(file, clientId, folderId, onImportProgress, requestId);

		dispatch(setCreationStatus(PublicationCreationStatus.PROCESSING));
		await dispatch(getPublicationCreationProgress(folderId, clientId, globalId, status));
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const createProposalFromDriveItem = (
	{ id, label }: Api.DriveItem,
	requestId: string,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: PublicationCreatorActionKeys.SET_REQUEST_ID,
			requestId,
		});
		dispatch(setCreationStatus(PublicationCreationStatus.IMPORTING));

		const {
			galleryInfos: { config: { clientId } },
		} = getState();

		const folderId = Utils.getFolderIdForNewResource();

		if (!folderId) {
			dispatch(hidePublicationCreator());
			return dispatch(createNotification('error.create_proposal_error'));
		}

		const { globalId, status } = await ApiUtils.createProposalFromDriveItem(folderId, clientId, id, label, requestId);

		dispatch(setCreationStatus(PublicationCreationStatus.PROCESSING));
		await dispatch(getPublicationCreationProgress(folderId, clientId, globalId, status));
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const setAsTemplate = (clientId: number, id: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		await ApiUtils.setAsTemplate(clientId, id);
		dispatch(addCustomTemplate(id));
		dispatch(createNotification('info.set_as_template_success'));
	} catch (error) {
		dispatch(createNotification('error.set_as_template_error'));
	}
};

const removeTemplateFromStore = (id: number): RemoveTemplateFromStoreAction => ({
	type: PublicationCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE,
	id,
});

export const removeTemplate = (clientId: number, id: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		await ApiUtils.removeTemplate(clientId, id);
		dispatch(removeCustomTemplate(id));
		dispatch(removeTemplateFromStore(id));
	} catch (error) {
		dispatch(createNotification('error.remove_template_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/PublicationCreator/actions.ts